import React from 'react';

/* ─── Button ─────────────────────────────────────────────── */
export function Btn({ children, onClick, variant='primary', size='md', disabled, style, type='button', icon, fullWidth }) {
  const base = {
    display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6,
    fontFamily:'inherit', fontWeight:600, borderRadius:'var(--radius-md)',
    border:'none', cursor:disabled?'not-allowed':'pointer',
    opacity:disabled?0.5:1, transition:'var(--transition)', whiteSpace:'nowrap',
    width: fullWidth ? '100%' : undefined,
  };
  const sizes = {
    xs:{ padding:'4px 10px', fontSize:11.5 },
    sm:{ padding:'5px 12px', fontSize:12 },
    md:{ padding:'9px 18px', fontSize:13.5 },
    lg:{ padding:'12px 24px', fontSize:15 },
  };
  const variants = {
    primary: { background:'var(--brand)', color:'#fff', boxShadow:'var(--shadow-brand)' },
    secondary:{ background:'var(--bg-elevated)', color:'var(--text-primary)', border:'1px solid var(--border-default)' },
    ghost: { background:'transparent', color:'var(--text-secondary)', border:'1px solid var(--border-subtle)' },
    danger: { background:'var(--accent-red-dim)', color:'var(--accent-red)', border:'1px solid rgba(239,68,68,0.2)' },
    success:{ background:'var(--accent-green-dim)', color:'var(--accent-green)', border:'1px solid rgba(34,197,94,0.2)' },
    info:   { background:'var(--accent-blue-dim)', color:'var(--accent-blue)', border:'1px solid rgba(59,130,246,0.2)' },
    amber:  { background:'var(--accent-amber-dim)', color:'var(--accent-amber)', border:'1px solid rgba(245,158,11,0.2)' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...sizes[size]||sizes.md, ...variants[variant]||variants.primary, ...style }}
      onMouseEnter={e=>{ if(!disabled) e.currentTarget.style.filter='brightness(1.12)'; }}
      onMouseLeave={e=>{ e.currentTarget.style.filter=''; }}>
      {icon && <span style={{fontSize:13,lineHeight:1}}>{icon}</span>}
      {children}
    </button>
  );
}

/* ─── IconBtn ────────────────────────────────────────────── */
export function IconBtn({ icon, onClick, title, variant='ghost', size=32 }) {
  return (
    <button onClick={onClick} title={title}
      style={{
        width:size, height:size, display:'flex', alignItems:'center', justifyContent:'center',
        background:'transparent', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-md)',
        color:'var(--text-secondary)', fontSize:13, transition:'var(--transition)', cursor:'pointer',
        flexShrink:0,
      }}
      onMouseEnter={e=>{ e.currentTarget.style.background='var(--bg-elevated)'; e.currentTarget.style.color='var(--text-primary)'; }}
      onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--text-secondary)'; }}>
      {icon}
    </button>
  );
}

/* ─── Badge ──────────────────────────────────────────────── */
export function Badge({ children, type='gray', dot, size='sm' }) {
  const styles = {
    success:{ bg:'var(--accent-green-dim)', color:'var(--accent-green)', border:'rgba(34,197,94,0.2)' },
    warning:{ bg:'var(--accent-amber-dim)', color:'var(--accent-amber)', border:'rgba(245,158,11,0.2)' },
    danger: { bg:'var(--accent-red-dim)',   color:'var(--accent-red)',   border:'rgba(239,68,68,0.2)' },
    info:   { bg:'var(--accent-blue-dim)',  color:'var(--accent-blue)',  border:'rgba(59,130,246,0.2)' },
    purple: { bg:'var(--accent-purple-dim)',color:'var(--accent-purple)',border:'rgba(168,85,247,0.2)' },
    cyan:   { bg:'var(--accent-cyan-dim)',  color:'var(--accent-cyan)',  border:'rgba(6,182,212,0.2)' },
    brand:  { bg:'var(--brand-dim)',        color:'var(--brand)',        border:'rgba(249,115,22,0.2)' },
    gray:   { bg:'rgba(255,255,255,0.06)',  color:'var(--text-secondary)',border:'rgba(255,255,255,0.1)' },
  };
  const s = styles[type] || styles.gray;
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5,
      padding:'3px 9px', borderRadius:'var(--radius-full)',
      fontSize:11, fontWeight:600, background:s.bg, color:s.color,
      border:`1px solid ${s.border}`, lineHeight:1.4, whiteSpace:'nowrap',
    }}>
      {dot && <span style={{ width:5, height:5, borderRadius:'50%', background:s.color, flexShrink:0 }} />}
      {children}
    </span>
  );
}

/* ─── Card ───────────────────────────────────────────────── */
export function Card({ children, style, glass, noPad, className }) {
  return (
    <div className={className} style={{
      background: glass ? 'rgba(17,21,32,0.85)' : 'var(--bg-surface)',
      backdropFilter: glass ? 'blur(12px)' : undefined,
      border:'1px solid var(--border-subtle)',
      borderRadius:'var(--radius-lg)',
      padding: noPad ? 0 : '20px 22px',
      ...style
    }}>
      {children}
    </div>
  );
}

/* ─── CardHeader ─────────────────────────────────────────── */
export function CardTitle({ children, action }) {
  return (
    <div style={{
      display:'flex', justifyContent:'space-between', alignItems:'center',
      marginBottom:18, paddingBottom:14, borderBottom:'1px solid var(--border-subtle)'
    }}>
      <span style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)', letterSpacing:'-0.01em' }}>{children}</span>
      {action}
    </div>
  );
}

/* ─── StatCard ───────────────────────────────────────────── */
export function StatCard({ label, value, icon, sub, color, trend, onClick }) {
  return (
    <div onClick={onClick} style={{
      background:'var(--bg-surface)', border:'1px solid var(--border-subtle)',
      borderRadius:'var(--radius-lg)', padding:'18px 20px',
      position:'relative', overflow:'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition:'var(--transition)',
    }}
    onMouseEnter={e=>{ if(onClick){ e.currentTarget.style.borderColor='var(--border-default)'; e.currentTarget.style.transform='translateY(-1px)'; }}}
    onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border-subtle)'; e.currentTarget.style.transform=''; }}>
      <div style={{ position:'absolute', top:0, right:0, width:72, height:72, borderRadius:'0 0 0 72px', background:color?`${color}10`:'var(--brand-dim)', display:'flex', alignItems:'flex-start', justifyContent:'flex-end', padding:'12px 12px 0 0', fontSize:20 }}>{icon}</div>
      <div style={{ fontSize:10.5, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:28, fontWeight:800, color:color||'var(--text-primary)', letterSpacing:'-0.03em', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11.5, color:'var(--text-muted)', marginTop:6 }}>{sub}</div>}
      {trend !== undefined && (
        <div style={{ fontSize:11, color:trend>0?'var(--accent-green)':'var(--accent-red)', marginTop:5, fontWeight:700 }}>
          {trend>0?'↑':'↓'} {Math.abs(trend)}%
        </div>
      )}
    </div>
  );
}

/* ─── PageHeader ─────────────────────────────────────────── */
export function PageHeader({ title, subtitle, action, breadcrumb }) {
  return (
    <div className="au" style={{ marginBottom:24 }}>
      {breadcrumb && <div style={{ fontSize:11.5, color:'var(--text-muted)', marginBottom:6, letterSpacing:'0.03em' }}>{breadcrumb}</div>}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.025em', lineHeight:1.2 }}>{title}</h1>
          {subtitle && <p style={{ fontSize:13, color:'var(--text-muted)', marginTop:4 }}>{subtitle}</p>}
        </div>
        {action && <div style={{ flexShrink:0 }}>{action}</div>}
      </div>
    </div>
  );
}

/* ─── Modal ──────────────────────────────────────────────── */
export function Modal({ title, onClose, children, maxWidth=560, subtitle }) {
  React.useEffect(() => {
    const esc = e => { if(e.key==='Escape') onClose(); };
    document.addEventListener('keydown', esc);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', esc); document.body.style.overflow = ''; };
  }, [onClose]);
  return (
    <div className="af" style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
      zIndex:200, display:'flex', alignItems:'center', justifyContent:'center',
      padding:'16px', backdropFilter:'blur(6px)'
    }} onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="as" style={{
        background:'var(--bg-surface)', border:'1px solid var(--border-default)',
        borderRadius:'var(--radius-xl)', padding:'24px 26px', width:'100%', maxWidth,
        maxHeight:'92vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,0.8)'
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
          <div>
            <h2 style={{ fontSize:17, fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.02em' }}>{title}</h2>
            {subtitle && <p style={{ fontSize:12.5, color:'var(--text-muted)', marginTop:3 }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} style={{
            background:'var(--bg-elevated)', border:'1px solid var(--border-default)',
            width:30, height:30, borderRadius:'var(--radius-md)', color:'var(--text-secondary)',
            fontSize:14, display:'flex', alignItems:'center', justifyContent:'center',
            transition:'var(--transition)', flexShrink:0, cursor:'pointer',
          }}
          onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
          onMouseLeave={e=>e.currentTarget.style.background='var(--bg-elevated)'}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ─── FormGroup ──────────────────────────────────────────── */
export function FormGroup({ label, children, hint, error, required }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
      {label && (
        <label style={{ fontSize:11.5, fontWeight:600, color:'var(--text-secondary)', letterSpacing:'0.02em' }}>
          {label}{required && <span style={{ color:'var(--brand)', marginLeft:3 }}>*</span>}
        </label>
      )}
      {children}
      {hint && !error && <span style={{ fontSize:11, color:'var(--text-muted)' }}>{hint}</span>}
      {error && <span style={{ fontSize:11, color:'var(--accent-red)', display:'flex', alignItems:'center', gap:4 }}>⚠ {error}</span>}
    </div>
  );
}

/* ─── FilterBar ──────────────────────────────────────────── */
export function FilterBar({ children, actions }) {
  return (
    <div className="au d1" style={{
      display:'flex', gap:10, marginBottom:16,
      flexWrap:'wrap', alignItems:'center',
    }}>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', flex:1, alignItems:'center' }}>
        {children}
      </div>
      {actions && <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>{actions}</div>}
    </div>
  );
}

/* ─── SearchInput ────────────────────────────────────────── */
export function SearchBar({ value, onChange, placeholder='Search...' }) {
  return (
    <div style={{ position:'relative', flexShrink:0, minWidth:200 }}>
      <span style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', fontSize:13, color:'var(--text-muted)', pointerEvents:'none' }}>⌕</span>
      <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        className="form-input" style={{ paddingLeft:32, paddingRight: value ? 32 : 14, minWidth:220 }} />
      {value && (
        <button onClick={()=>onChange('')} style={{
          position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
          background:'transparent', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:12,
        }}>✕</button>
      )}
    </div>
  );
}

/* ─── FilterSelect ───────────────────────────────────────── */
export function FilterSelect({ value, onChange, options, style }) {
  return (
    <select value={value} onChange={e=>onChange(e.target.value)}
      className="form-select" style={{ width:160, ...style }}>
      {options.map(o => (
        <option key={o.value||o} value={o.value||o}>{o.label||o}</option>
      ))}
    </select>
  );
}

/* ─── ActiveFilters ──────────────────────────────────────── */
export function ActiveFilters({ filters, onRemove }) {
  if (!filters || filters.length === 0) return null;
  return (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', marginBottom:12 }}>
      <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600 }}>Active:</span>
      {filters.map((f, i) => (
        <span key={i} style={{
          display:'inline-flex', alignItems:'center', gap:5,
          padding:'2px 8px', borderRadius:'var(--radius-full)',
          background:'var(--brand-dim)', border:'1px solid var(--brand-glow)',
          color:'var(--brand)', fontSize:11, fontWeight:600,
        }}>
          {f.label}
          <button onClick={()=>onRemove(f)} style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', fontSize:11, lineHeight:1, padding:0 }}>✕</button>
        </span>
      ))}
      <button onClick={()=>filters.forEach(onRemove)} style={{ fontSize:11, color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
        Clear all
      </button>
    </div>
  );
}

/* ─── DataTable ──────────────────────────────────────────── */
export function DataTable({ headers, children, empty, loading, compact }) {
  return (
    <div className="table-scroll">
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:compact?12.5:13.5 }}>
        <thead>
          <tr>
            {headers.map((h,i) => (
              <th key={i} style={{
                padding: compact ? '9px 14px' : '11px 16px',
                textAlign:'left', fontSize:10.5, fontWeight:700,
                color:'var(--text-muted)', textTransform:'uppercase',
                letterSpacing:'0.07em', borderBottom:'1px solid var(--border-subtle)',
                whiteSpace:'nowrap', background:'var(--bg-deep)',
                position:'sticky', top:0,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            [...Array(5)].map((_,i) => (
              <tr key={i}>
                {headers.map((_,j) => (
                  <td key={j} style={{ padding:'12px 16px', borderBottom:'1px solid var(--border-subtle)' }}>
                    <div className="skeleton" style={{ height:14, borderRadius:4, width:j===0?'80%':'60%' }} />
                  </td>
                ))}
              </tr>
            ))
          ) : children}
        </tbody>
      </table>
      {!loading && empty && (
        <div style={{ textAlign:'center', padding:'48px 20px', color:'var(--text-muted)' }}>
          <div style={{ fontSize:36, marginBottom:10, opacity:0.4 }}>📭</div>
          <div style={{ fontSize:14 }}>{empty}</div>
        </div>
      )}
    </div>
  );
}

/* ─── Td ─────────────────────────────────────────────────── */
export function Td({ children, style, mobileHide }) {
  return (
    <td style={{
      padding:'12px 16px', borderBottom:'1px solid var(--border-subtle)',
      verticalAlign:'middle', color:'var(--text-primary)', ...style
    }}
    className={mobileHide ? 'mobile-hide' : ''}>
      {children}
    </td>
  );
}

/* ─── Tr ─────────────────────────────────────────────────── */
export function Tr({ children, onClick, selected }) {
  return (
    <tr style={{
      transition:'background 0.1s',
      cursor: onClick ? 'pointer' : 'default',
      background: selected ? 'var(--brand-dim)' : '',
    }}
    onMouseEnter={e=>{ if(!selected) e.currentTarget.style.background='var(--bg-elevated)'; }}
    onMouseLeave={e=>{ if(!selected) e.currentTarget.style.background=''; }}
    onClick={onClick}>
      {children}
    </tr>
  );
}

/* ─── Paginator ──────────────────────────────────────────── */
export function Paginator({ page, totalPages, totalItems, perPage, onPage, onPerPage }) {
  if (totalPages <= 1 && !onPerPage) return null;

  const pages = [];
  const WINDOW = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - WINDOW && i <= page + WINDOW)) {
      pages.push(i);
    }
  }
  // Insert ellipses
  const withEllipsis = [];
  let prev = 0;
  for (const p of pages) {
    if (prev && p - prev > 1) withEllipsis.push('…');
    withEllipsis.push(p);
    prev = p;
  }

  const btnStyle = (active, disabled) => ({
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    minWidth:32, height:32, padding:'0 8px',
    borderRadius:'var(--radius-md)', border:'1px solid',
    fontSize:12.5, fontWeight:600, cursor: disabled ? 'not-allowed' : 'pointer',
    transition:'var(--transition)',
    background: active ? 'var(--brand)' : 'transparent',
    borderColor: active ? 'var(--brand)' : 'var(--border-subtle)',
    color: active ? '#fff' : disabled ? 'var(--text-muted)' : 'var(--text-secondary)',
    opacity: disabled ? 0.4 : 1,
  });

  return (
    <div style={{
      display:'flex', alignItems:'center', justifyContent:'space-between',
      padding:'14px 16px', borderTop:'1px solid var(--border-subtle)',
      flexWrap:'wrap', gap:10,
    }}>
      <div style={{ fontSize:12, color:'var(--text-muted)', fontWeight:500 }}>
        {totalItems > 0 && (
          <>Showing {Math.min((page-1)*perPage+1, totalItems)}–{Math.min(page*perPage, totalItems)} of <strong style={{color:'var(--text-secondary)'}}>{totalItems}</strong></>
        )}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
        {onPerPage && (
          <select value={perPage} onChange={e=>onPerPage(Number(e.target.value))}
            style={{ ...btnStyle(false,false), width:'auto', padding:'0 28px 0 10px', appearance:'none',
              backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='7' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23505d7a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E\")",
              backgroundRepeat:'no-repeat', backgroundPosition:'right 8px center',
            }}>
            {[10,20,50,100].map(n=><option key={n} value={n}>{n}/page</option>)}
          </select>
        )}
        <button style={btnStyle(false, page===1)} disabled={page===1} onClick={()=>onPage(page-1)}>‹</button>
        {withEllipsis.map((p,i) =>
          p === '…'
            ? <span key={`e${i}`} style={{ fontSize:13, color:'var(--text-muted)', padding:'0 4px' }}>…</span>
            : <button key={p} style={btnStyle(p===page, false)} onClick={()=>onPage(p)}>{p}</button>
        )}
        <button style={btnStyle(false, page===totalPages)} disabled={page===totalPages} onClick={()=>onPage(page+1)}>›</button>
      </div>
    </div>
  );
}

/* ─── ResultCount ────────────────────────────────────────── */
export function ResultCount({ count, total, label='result' }) {
  return (
    <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:500, whiteSpace:'nowrap' }}>
      {count !== total && total > 0 ? `${count} of ${total}` : count} {label}{count!==1?'s':''}
    </span>
  );
}

/* ─── Divider ────────────────────────────────────────────── */
export function Divider({ label }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, margin:'6px 0' }}>
      <div style={{ flex:1, height:1, background:'var(--border-subtle)' }} />
      {label && <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase' }}>{label}</span>}
      <div style={{ flex:1, height:1, background:'var(--border-subtle)' }} />
    </div>
  );
}

/* ─── Avatar ─────────────────────────────────────────────── */
export function Avatar({ name, size=32, color }) {
  const initials = name?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() || '?';
  const COLORS = ['#f97316','#3b82f6','#22c55e','#a855f7','#06b6d4','#f59e0b','#ef4444'];
  const bg = color || COLORS[name?.charCodeAt(0) % COLORS.length] || COLORS[0];
  return (
    <div style={{
      width:size, height:size, borderRadius:'50%',
      background:`${bg}20`, border:`1.5px solid ${bg}40`,
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:size*0.34, fontWeight:700, color:bg, flexShrink:0, letterSpacing:'-0.02em',
    }}>
      {initials}
    </div>
  );
}

/* ─── Spinner ────────────────────────────────────────────── */
export function Spinner({ size=20, label }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:40, gap:12 }}>
      <div style={{ width:size, height:size, border:'2px solid var(--border-default)', borderTopColor:'var(--brand)', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
      {label && <span style={{ fontSize:12.5, color:'var(--text-muted)' }}>{label}</span>}
    </div>
  );
}

/* ─── Empty ──────────────────────────────────────────────── */
export function Empty({ icon='📭', title='Nothing here', desc, action }) {
  return (
    <div style={{ textAlign:'center', padding:'52px 20px' }}>
      <div style={{ fontSize:40, marginBottom:12, opacity:0.4 }}>{icon}</div>
      <div style={{ fontSize:15, fontWeight:600, color:'var(--text-secondary)', marginBottom:6 }}>{title}</div>
      {desc && <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:16 }}>{desc}</div>}
      {action}
    </div>
  );
}

/* ─── Toggle ─────────────────────────────────────────────── */
export function Toggle({ checked, onChange, disabled }) {
  return (
    <div onClick={()=>!disabled&&onChange(!checked)} style={{
      width:38, height:22, borderRadius:99,
      background:checked?'var(--brand)':'var(--bg-hover)',
      border:`1px solid ${checked?'var(--brand)':'var(--border-default)'}`,
      position:'relative', cursor:disabled?'not-allowed':'pointer',
      opacity:disabled?0.5:1, transition:'var(--transition)', flexShrink:0,
    }}>
      <div style={{
        width:16, height:16, borderRadius:'50%', background:'white',
        position:'absolute', top:2, left:checked?18:2,
        transition:'left 0.18s cubic-bezier(.4,0,.2,1)', boxShadow:'0 1px 3px rgba(0,0,0,0.4)',
      }} />
    </div>
  );
}

/* ─── InfoRow ────────────────────────────────────────────── */
export function InfoRow({ label, value, mono }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border-subtle)' }}>
      <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:500 }}>{label}</span>
      <span style={{ fontSize:13, color:'var(--text-primary)', fontWeight:600, fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>{value}</span>
    </div>
  );
}

/* ─── SectionHeader ──────────────────────────────────────── */
export function SectionHeader({ title, desc, icon, action }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, paddingBottom:14, borderBottom:'1px solid var(--border-subtle)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        {icon && <span style={{ fontSize:18 }}>{icon}</span>}
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)' }}>{title}</div>
          {desc && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{desc}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}
